import type { User } from '../types';

export type TransferCustomerRow = {
  bpCode: string;
  customerName: string;
  lineOfBusiness?: string;
};

function normalizeItemArray(raw: unknown): Record<string, unknown>[] {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw as Record<string, unknown>[];
  return [raw as Record<string, unknown>];
}

function rowFromCompany(entry: Record<string, unknown>): TransferCustomerRow | null {
  const bpCode = String(
    entry.bpCode ??
      entry.BP_Code ??
      entry.BpCode ??
      entry.CustomerCode ??
      entry.CardCode ??
      entry.To_Customer ??
      ''
  ).trim();
  const customerName = String(
    entry.CustomerName ??
      entry.CustName ??
      entry.Name ??
      entry.CardName ??
      entry.To_Customer_Name ??
      bpCode
  ).trim();
  if (!bpCode && !customerName) return null;
  const lineOfBusiness = String(entry.LOB ?? entry.LoB ?? entry.lineOfBusiness ?? '').trim();
  return {
    bpCode: bpCode || customerName,
    customerName: customerName || bpCode,
    lineOfBusiness: lineOfBusiness || undefined,
  };
}

/** Legacy transfer screens: selectable customers come from IAM `user.companies`. */
export function transferCustomersFromUser(user: User | null | undefined): TransferCustomerRow[] {
  const raw = user?.companies;
  const list = normalizeItemArray(raw);
  const mapped = list
    .map((e) => rowFromCompany(e))
    .filter((r): r is TransferCustomerRow => r != null);

  const fmcg = mapped.filter((c) => c.lineOfBusiness === 'FMCG');
  if (fmcg.length > 0) return fmcg.sort((a, b) => a.customerName.localeCompare(b.customerName));
  return mapped.sort((a, b) => a.customerName.localeCompare(b.customerName));
}

/** Parse GetFlow_ToCustomers destination list (requires Customer_No). */
export function parseGetFlowToCustomersResponse(data: unknown): TransferCustomerRow[] {
  if (data == null || typeof data !== 'object') return [];
  const d = data as Record<string, unknown>;

  const paths: unknown[] = [
    (d.Customer_Data as Record<string, unknown> | undefined)?.Item,
    (d.Response_Data as Record<string, unknown> | undefined)?.Customer_Data,
    ((d.Response_Data as Record<string, unknown> | undefined)?.Customers as Record<string, unknown> | undefined)
      ?.Customer,
    (d.Response_Data as Record<string, unknown> | undefined)?.Item,
    d.Customer,
    d.Item,
  ];

  for (const raw of paths) {
    const nested =
      raw && typeof raw === 'object' && !Array.isArray(raw)
        ? (raw as Record<string, unknown>).Item ?? raw
        : raw;
    const rows = normalizeItemArray(nested)
      .map((e) => rowFromCompany(e))
      .filter((r): r is TransferCustomerRow => r != null);
    if (rows.length > 0) return rows;
  }

  return [];
}

export function pickCompaniesFromAuthPayload(payload: unknown): Record<string, unknown>[] | undefined {
  if (payload == null || typeof payload !== 'object') return undefined;
  const visited = new Set<unknown>();

  function walk(node: unknown, depth: number): Record<string, unknown>[] | undefined {
    if (depth > 8 || node == null || typeof node !== 'object') return undefined;
    if (visited.has(node)) return undefined;
    visited.add(node);

    if (Array.isArray(node)) {
      if (node.length > 0 && node.every((x) => x && typeof x === 'object')) {
        const first = node[0] as Record<string, unknown>;
        if (
          'bpCode' in first ||
          'CustomerCode' in first ||
          'CardCode' in first ||
          'CustomerName' in first
        ) {
          return node as Record<string, unknown>[];
        }
      }
      for (const item of node) {
        const found = walk(item, depth + 1);
        if (found?.length) return found;
      }
      return undefined;
    }

    const rec = node as Record<string, unknown>;
    if (Array.isArray(rec.companies) && rec.companies.length > 0) {
      return rec.companies as Record<string, unknown>[];
    }
    const user = rec.user;
    if (user && typeof user === 'object' && Array.isArray((user as Record<string, unknown>).companies)) {
      return (user as Record<string, unknown>).companies as Record<string, unknown>[];
    }
    const dataNode = rec.data;
    if (dataNode && typeof dataNode === 'object') {
      const found = walk(dataNode, depth + 1);
      if (found?.length) return found;
    }
    for (const v of Object.values(rec)) {
      const found = walk(v, depth + 1);
      if (found?.length) return found;
    }
    return undefined;
  }

  return walk(payload, 0);
}
